/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, FieldErrors, FieldError } from "react-hook-form";
import { If, Then, Else, When } from "react-if";
import { Input, Label, Button, Sidebar, theme, Text } from "@nordcloud/gnui";
import { useFeature } from "~/hooks/useFeature";
import { showError } from "~/services/toast";
import { isEmpty } from "~/tools";
import { resourceGroupSchema } from "./validators";

type FormData = {
  value: string;
};

type Props = {
  onSubmit: (value: string) => void;
  onClose: () => void;
  isOpen: boolean;
  isIbmResourceGroup?: boolean;
};

export function AddResourceGroupForm({
  onSubmit,
  onClose,
  isOpen,
  isIbmResourceGroup,
}: Props) {
  const useExperimental = useFeature("allowExperimental");
  const { register, handleSubmit, watch } = useForm({
    resolver: yupResolver(resourceGroupSchema),
    defaultValues: {
      value: "",
    },
  });

  const onError = (error: FieldErrors) => {
    Object.values(error).forEach((err: FieldError) =>
      err.message ? showError(err.message) : undefined
    );
  };

  const onUpdate = (data: FormData) => {
    onSubmit(data.value);
  };

  const watcher = watch();
  const disabled = isEmpty(watcher.value);
  const prefix = isIbmResourceGroup ? "" : "non-";
  const title = `Add Resource Group (${prefix}IBM Cloud)`;

  return (
    <Sidebar title={title} isOpen={isOpen} onClick={onClose}>
      <Text size="sm">
        <When condition={!isIbmResourceGroup}>
          <If condition={useExperimental}>
            <Then>
              Add one of: <br />
              - Azure Resource Group
              <br />
              - Kubernetes Namespace
              <br />- External Integration ID
            </Then>
            <Else>
              Works with Azure Resource Group and Kubernetes Namespace
            </Else>
          </If>
        </When>
      </Text>
      <form onSubmit={handleSubmit(onUpdate, onError)}>
        <div
          css={{
            marginBottom: theme.spacing.spacing04,
          }}
        >
          <Label htmlFor="resource-group-input" name="Identifier" required />
          <Input
            id="resource-group-input"
            {...register("value")}
            type="text"
            placeholder="Name or ID"
          />
        </div>
        <Button disabled={disabled} type="submit">
          Add
        </Button>
      </form>
    </Sidebar>
  );
}
